/**
 * AE Boat Field — "Tell us your boat(s)" line-item property.
 *
 * Three jobs:
 *   1. Grow the single-line textarea as the customer types, up to a cap.
 *   2. Preserve what they typed across variant-change section re-renders.
 *   3. Enforce the optional "required" toggle on BOTH add-to-cart paths.
 *
 * On (3): the inline ATC is type="submit" and would validate natively, but the
 * sticky ATC is type="button" driven by the Shrine bundle and never triggers
 * native validation. Rather than have one path show a browser bubble and the
 * other show nothing, the field carries no HTML `required` attribute and this
 * capture-phase handler is the single enforcement path for both. Shrine has its
 * own data-required-fields counter mechanism; we deliberately do not touch it —
 * it lives in an obfuscated vendor bundle and is not a stable contract.
 */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'ae:boat-field:';
  var ATC_SELECTOR = '.main-product-atc, .product-form__submit, .atc-button';

  function storageKey(field) {
    return STORAGE_PREFIX + (field.dataset.storageKey || field.id);
  }

  function readStored(field) {
    try {
      return window.sessionStorage.getItem(storageKey(field)) || '';
    } catch (error) {
      return '';
    }
  }

  function writeStored(field) {
    try {
      if (field.value) {
        window.sessionStorage.setItem(storageKey(field), field.value);
      } else {
        window.sessionStorage.removeItem(storageKey(field));
      }
    } catch (error) {
      /* Private mode or storage full — persistence is a nicety, not a requirement. */
    }
  }

  function autoGrow(field) {
    var max = parseInt(field.dataset.maxHeight, 10) || 200;

    field.style.height = 'auto';

    var next = Math.min(field.scrollHeight, max);
    field.style.height = next + 'px';
    field.style.overflowY = field.scrollHeight > max ? 'auto' : 'hidden';
  }

  function wrapper(field) {
    return field.closest('.ae-boat-field');
  }

  function showError(field, message) {
    var root = wrapper(field);
    if (!root) return;

    var error = root.querySelector('[data-ae-boat-field-error]');
    root.classList.add('is-invalid');

    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  function clearError(field) {
    var root = wrapper(field);
    if (!root) return;

    var error = root.querySelector('[data-ae-boat-field-error]');
    root.classList.remove('is-invalid');

    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  function isRequired(field) {
    return field.dataset.required === 'true';
  }

  function isBlank(field) {
    return field.value.trim() === '';
  }

  /**
   * Shopify stores a blank line-item property as "" rather than discarding it,
   * so an untouched box would attach an empty "Boat(s)" to every order. The cart
   * templates hide blank properties, but the admin order still carries them.
   * An input with no name attribute is not serialised at all, so drop the name
   * while the box is empty and restore it as soon as something is typed.
   */
  function syncName(field) {
    var name = field.dataset.propertyName;
    if (!name) return;

    if (isBlank(field)) {
      field.removeAttribute('name');
    } else if (field.getAttribute('name') !== name) {
      field.setAttribute('name', name);
    }
  }

  /** Fields owned by the form the given add-to-cart button submits. */
  function fieldsForButton(button) {
    var formId = button.getAttribute('form');

    if (!formId) {
      var form = button.closest('form');
      /**
       * getAttribute('id'), never form.id. HTMLFormElement exposes its named
       * controls as own properties, which shadow the inherited id accessor —
       * and every Shopify product form contains <input name="id"> for the
       * variant, so form.id returns that input element rather than a string.
       */
      formId = form ? form.getAttribute('id') : null;
    }

    if (!formId) return [];

    return Array.prototype.filter.call(
      document.querySelectorAll('[data-ae-boat-field]'),
      function (field) {
        return field.dataset.formId === formId;
      }
    );
  }

  function blockAddToCart(event) {
    var button = event.target.closest ? event.target.closest(ATC_SELECTOR) : null;
    if (!button) return;

    var invalid = fieldsForButton(button).filter(function (field) {
      return isRequired(field) && isBlank(field);
    });

    if (!invalid.length) return;

    event.preventDefault();
    event.stopPropagation();

    invalid.forEach(function (field) {
      showError(field, 'Please tell us your boat(s) before adding to cart.');
    });

    invalid[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
    invalid[0].focus({ preventScroll: true });
  }

  function init(field) {
    if (field.dataset.aeBoatFieldReady === 'true') return;
    field.dataset.aeBoatFieldReady = 'true';

    var stored = readStored(field);
    if (stored && !field.value) field.value = stored;

    autoGrow(field);
    syncName(field);

    field.addEventListener('input', function () {
      autoGrow(field);
      syncName(field);
      writeStored(field);
      if (!isBlank(field)) clearError(field);
    });

    field.addEventListener('blur', function () {
      if (isRequired(field) && isBlank(field)) {
        showError(field, 'Please tell us your boat(s) before adding to cart.');
      }
    });

  }

  function start() {
    document.querySelectorAll('[data-ae-boat-field]').forEach(init);
  }

  /* start() runs off a subtree observer, so coalesce bursts into one rAF tick. */
  var scheduled = false;
  function scheduleStart() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      start();
    });
  }

  /* Capture phase so we run before the Shrine bundle's own click handling. */
  document.addEventListener('click', blockAddToCart, true);

  /* Clear the saved value once the add actually succeeds. */
  document.addEventListener('cart:refresh', function () {
    document.querySelectorAll('[data-ae-boat-field]').forEach(function (field) {
      field.value = '';
      writeStored(field);
      autoGrow(field);
      syncName(field);
      clearError(field);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Variant changes re-render parts of the product section; re-bind new nodes. */
  if (window.MutationObserver) {
    new window.MutationObserver(scheduleStart).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:block:select', start);
})();
